/**
 * Stitch a completed render job into one continuous video, client-side.
 * Chunk mp4s play back-to-back onto a canvas (captureStream) while the ORIGINAL
 * voice-over files play in sequence through WebAudio into the same recording —
 * studio-quality VO with no provider re-encode, no lip-sync drift. Real-time, like
 * the animatic exporter.
 */

export interface StitchSegment {
  /** Same-origin URL of the chunk's mp4 (asset-store key URL). */
  videoUrl: string;
  label: string;
}

export interface StitchProgress {
  elapsedSeconds: number;
  totalSeconds: number;
  segmentLabel: string;
}

function pickMime(): { mimeType: string; extension: "mp4" | "webm" } | null {
  const candidates: Array<{ mimeType: string; extension: "mp4" | "webm" }> = [
    { mimeType: "video/mp4;codecs=avc1.42E01E,mp4a.40.2", extension: "mp4" },
    { mimeType: "video/mp4", extension: "mp4" },
    { mimeType: "video/webm;codecs=vp9,opus", extension: "webm" },
    { mimeType: "video/webm", extension: "webm" }
  ];
  for (const candidate of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(candidate.mimeType)) return candidate;
    } catch {
      // keep trying
    }
  }
  return null;
}

function loadVideo(url: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => resolve(video);
    video.onerror = () => reject(new Error(`Couldn't load ${url.split("/").pop()}`));
    video.src = url;
  });
}

export async function stitchRenderJob(
  segments: StitchSegment[],
  voiceUrls: string[],
  options: { title: string; width?: number; height?: number; onProgress?: (p: StitchProgress) => void; signal?: AbortSignal } = { title: "Stitched cut" }
): Promise<{ blob: Blob; extension: "mp4" | "webm" }> {
  const picked = pickMime();
  if (!picked) throw new Error("This browser can't record video. Try Chrome or Safari.");
  if (!segments.length) throw new Error("No finished chunks to stitch.");

  const videos = await Promise.all(segments.map((segment) => loadVideo(segment.videoUrl)));
  const width = options.width || 1280;
  const height = options.height || 720;
  const totalSeconds = videos.reduce((sum, video) => sum + (video.duration || 0), 0);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas isn't available.");
  const ctx = context;
  const stream = canvas.captureStream(30);

  // Voice-over master track: the original files, in order, mixed into the recording.
  let audioContext: AudioContext | null = null;
  const voiceElements: HTMLAudioElement[] = [];
  if (voiceUrls.length) {
    audioContext = new AudioContext();
    void audioContext.resume().catch(() => undefined);
    const destination = audioContext.createMediaStreamDestination();
    destination.stream.getAudioTracks().forEach((track) => stream.addTrack(track));
    for (const url of voiceUrls) {
      const element = new Audio(url);
      element.preload = "auto";
      const source = audioContext.createMediaElementSource(element);
      source.connect(destination);
      source.connect(audioContext.destination);
      voiceElements.push(element);
    }
  }

  const recorder = new MediaRecorder(stream, { mimeType: picked.mimeType, videoBitsPerSecond: 8_000_000 });
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size) chunks.push(event.data);
  };

  return new Promise((resolve, reject) => {
    let raf = 0;
    let finished = false;
    let segmentIndex = 0;
    let elapsedBefore = 0;
    const startedAt = performance.now();

    function cleanup() {
      cancelAnimationFrame(raf);
      videos.forEach((video) => video.pause());
      voiceElements.forEach((element) => {
        element.onended = null;
        element.pause();
      });
      void audioContext?.close().catch(() => undefined);
      stream.getTracks().forEach((track) => track.stop());
    }

    function fail(error: Error) {
      if (finished) return;
      finished = true;
      try { recorder.stop(); } catch { /* stopped */ }
      cleanup();
      reject(error);
    }

    recorder.onstop = () => {
      cleanup();
      resolve({ blob: new Blob(chunks, { type: picked.mimeType.split(";")[0] }), extension: picked.extension });
    };
    recorder.onerror = () => fail(new Error("Recording failed partway through."));
    options.signal?.addEventListener("abort", () => fail(new DOMException("Cancelled", "AbortError")), { once: true });

    // Chain the VO files end to end.
    if (voiceElements.length) {
      for (let i = 0; i < voiceElements.length - 1; i += 1) {
        voiceElements[i].onended = () => void voiceElements[i + 1].play().catch(() => undefined);
      }
    }

    function playSegment(index: number) {
      if (index >= videos.length) {
        if (!finished) {
          finished = true;
          try { recorder.stop(); } catch { /* stopped */ }
        }
        return;
      }
      const video = videos[index];
      video.currentTime = 0;
      void video.play().catch(() => fail(new Error(`Playback failed on ${segments[index].label}`)));
      video.onended = () => {
        elapsedBefore += video.duration || 0;
        segmentIndex += 1;
        playSegment(segmentIndex);
      };
    }

    function draw() {
      const video = videos[Math.min(segmentIndex, videos.length - 1)];
      if (video && video.readyState >= 2) {
        const cover = Math.max(width / video.videoWidth, height / video.videoHeight);
        const dw = video.videoWidth * cover;
        const dh = video.videoHeight * cover;
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(video, (width - dw) / 2, (height - dh) / 2, dw, dh);
      }
      options.onProgress?.({
        elapsedSeconds: Math.min(totalSeconds, (performance.now() - startedAt) / 1000),
        totalSeconds,
        segmentLabel: segments[Math.min(segmentIndex, segments.length - 1)]?.label || ""
      });
      raf = requestAnimationFrame(draw);
    }

    recorder.start(1000);
    if (voiceElements.length) void voiceElements[0].play().catch(() => undefined);
    playSegment(0);
    raf = requestAnimationFrame(draw);
  });
}

export function downloadStitch(result: { blob: Blob; extension: string }, title: string) {
  const safe = (title || "kinetix-cut").replace(/[\\/:*?"<>|]+/g, "").trim() || "kinetix-cut";
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${safe}.${result.extension}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
